'use client';

import { createContext, type JSX, PropsWithChildren, useContext } from 'react';

import { UseFormWithRecaptcha } from './useFormWithRecaptcha';

const context = createContext<UseFormWithRecaptcha | null>(null);

/**
 * Shares a form's reCAPTCHA state (from useFormWithRecaptcha) with everything it renders, so any
 * descendant can read canSubmit / the v2 challenge via useRecaptchaForm() without prop drilling.
 * It does NOT render a <form> — a form that uses reCAPTCHA calls the hook and wraps its own subtree
 * in this provider.
 */
export function RecaptchaFormProvider({
    value,
    children,
}: PropsWithChildren<{ value: UseFormWithRecaptcha }>): JSX.Element {
    return <context.Provider value={value}>{children}</context.Provider>;
}

/**
 * Reads the reCAPTCHA state published by the nearest <RecaptchaFormProvider>. Throws if used
 * outside one, so a missing provider fails loudly rather than silently disabling the challenge.
 */
export function useRecaptchaForm(): UseFormWithRecaptcha {
    const value = useContext(context);
    if (!value) {
        throw new Error('useRecaptchaForm must be used within a <RecaptchaFormProvider>');
    }
    return value;
}
